
import React, { useState, useEffect } from 'react';
import { PrayerRequest, PrayerType, PrayerStatus, PrayerList } from '../types';
import { CloseIcon } from './icons';

interface PrayerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (prayerData: Omit<PrayerRequest, 'id' | 'date'>) => void;
  prayerToEdit: PrayerRequest | null;
  lists: PrayerList[];
}

const PrayerFormModal: React.FC<PrayerFormModalProps> = ({ isOpen, onClose, onSubmit, prayerToEdit, lists }) => {
  const [formData, setFormData] = useState({
    title: '',
    requestor: 'Me',
    details: '',
    type: PrayerType.PETITION,
    status: PrayerStatus.CURRENT,
    answeredDetails: '',
    listId: lists[0]?.id || '1',
  });

  useEffect(() => {
    if (prayerToEdit) {
      setFormData({
        title: prayerToEdit.title,
        requestor: prayerToEdit.requestor,
        details: prayerToEdit.details,
        type: prayerToEdit.type,
        status: prayerToEdit.status,
        answeredDetails: prayerToEdit.answeredDetails || '',
        listId: prayerToEdit.listId,
      });
    } else {
      setFormData({
        title: '', requestor: 'Me', details: '',
        type: PrayerType.PETITION, status: PrayerStatus.CURRENT,
        answeredDetails: '', listId: lists[0]?.id || '1',
      });
    }
  }, [prayerToEdit, isOpen, lists]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.details) return;
    const { answeredDetails, ...rest } = formData;
    onSubmit({
        ...rest,
        ...(formData.status === PrayerStatus.ANSWERED && { answeredDetails }),
    });
  };

  const inputStyles = "mt-1 block w-full bg-white text-gray-900 placeholder-gray-500 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white";
  const labelStyles = "block text-sm font-medium text-gray-700 dark:text-gray-300";

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-auto relative flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{prayerToEdit ? 'Edit Prayer' : 'New Prayer Request'}</h2>
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition">
                <CloseIcon className="w-7 h-7" />
            </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-6 space-y-4">
          <div>
            <label htmlFor="title" className={labelStyles}>Title</label>
            <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} className={inputStyles} required />
          </div>
          <div>
            <label htmlFor="requestor" className={labelStyles}>Requestor</label>
            <input type="text" name="requestor" id="requestor" value={formData.requestor} onChange={handleChange} className={inputStyles} required />
          </div>
          <div>
            <label htmlFor="details" className={labelStyles}>Details</label>
            <textarea name="details" id="details" value={formData.details} onChange={handleChange} rows={4} className={inputStyles} required></textarea>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <div>
                <label htmlFor="type" className={labelStyles}>Type</label>
                <select name="type" id="type" value={formData.type} onChange={handleChange} className={inputStyles}>
                    {Object.values(PrayerType).map(type => <option key={type} value={type}>{type}</option>)}
                </select>
            </div>
             <div>
                <label htmlFor="listId" className={labelStyles}>List</label>
                <select name="listId" id="listId" value={formData.listId} onChange={handleChange} className={inputStyles}>
                    {lists.map(list => <option key={list.id} value={list.id}>{list.name}</option>)}
                </select>
            </div>
          </div>
          {prayerToEdit && (
             <div>
                <label htmlFor="status" className={labelStyles}>Status</label>
                <select name="status" id="status" value={formData.status} onChange={handleChange} className={inputStyles}>
                    {Object.values(PrayerStatus).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
          )}
          {formData.status === PrayerStatus.ANSWERED && (
             <div>
                <label htmlFor="answeredDetails" className={labelStyles}>Answer Details</label>
                <textarea name="answeredDetails" id="answeredDetails" value={formData.answeredDetails} onChange={handleChange} rows={3} className={inputStyles}></textarea>
            </div>
          )}
        </form>
        <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 font-semibold py-2 px-4 rounded-md border border-gray-300 dark:border-gray-500 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-500">Cancel</button>
            <button type="submit" onClick={handleSubmit} className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-md shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-300 dark:focus:ring-indigo-800">
                {prayerToEdit ? 'Save Changes' : 'Add Prayer'}
            </button>
        </div>
      </div>
    </div>
  );
};

export default PrayerFormModal;